import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertTriangle,
  Check,
  CreditCard,
  ExternalLink,
  FileText,
  Loader2,
} from 'lucide-react';
import {
  apiClient,
  type AccountCardView,
  type AccountSubscriptionResponse,
  type AccountSubscriptionStatus,
  type ApiError,
  type PublicPlan,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { PaymentStep, type PaymentConfirmedInfo } from '@/pages/assinar/payment-step';

/**
 * Passo B — area de assinatura do cliente (round 2 frontend).
 *
 * Consome os endpoints autenticados de apps/api/src/modules/billing/
 * subscription.controller.ts: GET /account/subscription, GET
 * /account/invoices, POST /account/billing-portal, POST /account/subscribe.
 * A maior parte da tela e READ-ONLY em relacao ao billing (abrir o Billing
 * Portal do Stripe) — a excecao e o fluxo de "Escolher plano" abaixo
 * (rodada 2), que cria a assinatura self-service quando o tenant ainda nao
 * tem plano.
 *
 * Rota /escritorio/configuracoes/assinatura ja herda RequireAdmin do bloco
 * pai em App.tsx (mesma protecao das demais paginas de configuracoes).
 *
 * Fluxo de assinatura (sub.plan === null): reusa o <PaymentStep> do
 * checkout anonimo (apps/web/src/pages/assinar/payment-step.tsx) — ele ja e
 * agnostico da origem do clientSecret. Duas diferencas do fluxo anonimo:
 *   - returnUrl aponta de volta pra esta pagina (nao /checkout/sucesso, que
 *     tem CTA "Ir para o login" sem sentido pra quem ja esta logado);
 *   - onConfirmed usa `redirect:'if_required'` no Payment Element — quando
 *     o metodo escolhido nao exige navegacao (maioria dos cartoes sem
 *     3DS), o controle volta pro JS e comecamos o polling AQUI MESMO, sem
 *     reload. Metodos que exigem redirect real (3DS/Pix/boleto) navegam
 *     ate o returnUrl de qualquer forma; ao voltar, `redirect_status` na
 *     query string reinicia o polling (ver useEffect abaixo).
 */

type SubscribeFlowState =
  | { kind: 'choosing' }
  | { kind: 'creating' }
  | { kind: 'paying'; plan: PublicPlan; clientSecret: string; mode: 'payment' | 'setup' }
  | { kind: 'polling' }
  | { kind: 'unlocking' };

/** Passo B rodada 3 — trocar o cartao default via SetupIntent (Payment Element modo setup). */
type CardFlowState =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'collecting'; clientSecret: string }
  | { kind: 'saving' };

/** Passo B rodada 3 — retry do pagamento em atraso (Payment Element modo payment). */
type RetryPayFlowState =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'paying'; clientSecret: string }
  | { kind: 'polling' }
  | { kind: 'unlocking' }
  | { kind: 'timeout' }
  | { kind: 'nothing-to-pay' };

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30_000;

/** Passo B rodada 4 — reintento do re-bootstrap de billing pos-pagamento. */
const UNLOCK_RETRY_INTERVAL_MS = 3000;
const UNLOCK_MAX_ATTEMPTS = 5;

/**
 * Rodada 4 (bloqueio por inadimplencia): apos o pagamento confirmar, o
 * `billing` usado pelo gate global (RequireActiveSubscription, populado por
 * GET /auth/me) pode levar ate ~60s pra refletir a liberacao (cache no
 * backend). Re-bootstrap o auth-store algumas vezes ate `billingBlocked`
 * cair. Desiste silenciosamente apos UNLOCK_MAX_ATTEMPTS — o usuario segue
 * na tela de assinatura de qualquer forma (nunca preso sem saida); o gate
 * reavalia no proximo bootstrap ou na proxima chamada 402.
 */
async function waitForBillingUnlock(): Promise<void> {
  for (let attempt = 0; attempt < UNLOCK_MAX_ATTEMPTS; attempt++) {
    try {
      await useAuthStore.getState().bootstrap();
    } catch {
      // Falha pontual — tenta de novo no proximo attempt.
    }
    if (!useAuthStore.getState().billingBlocked) return;
    if (attempt < UNLOCK_MAX_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, UNLOCK_RETRY_INTERVAL_MS));
    }
  }
}

/**
 * Poll GET /account/subscription ate `isDone(sub)` ser true, ou estourar
 * POLL_TIMEOUT_MS. Reusado pelo fluxo de assinar plano novo (aguarda
 * `plan != null`) e pelo retry de fatura em atraso (aguarda
 * `status === 'active'`). Retorna a funcao de cleanup do polling.
 */
function pollAccountSubscription(opts: {
  queryClient: QueryClient;
  isDone: (sub: AccountSubscriptionResponse) => boolean;
  onDone: () => void;
  onTimeout: () => void;
}): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout>;
  const startedAt = Date.now();

  async function tick() {
    if (cancelled) return;
    try {
      const latest = await apiClient.getAccountSubscription();
      if (cancelled) return;
      opts.queryClient.setQueryData(['account-subscription'], latest);
      if (opts.isDone(latest)) {
        opts.onDone();
        return;
      }
    } catch {
      // Falha pontual de rede — tenta de novo no proximo tick.
    }
    if (cancelled) return;
    if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
      opts.onTimeout();
      return;
    }
    timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
  }

  timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const priceFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function formatCents(cents: number): string {
  return priceFormatter.format(cents / 100);
}

const INTERVAL_LABEL: Record<string, string> = {
  month: '/mês',
  year: '/ano',
};

function formatDateLong(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return format(new Date(iso), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return null;
  }
}

function formatDateShort(iso: string): string {
  try {
    return format(new Date(iso), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return iso;
  }
}

const STATUS_LABEL: Record<AccountSubscriptionStatus, string> = {
  active: 'Ativa',
  trial: 'Em teste',
  past_due: 'Pagamento em atraso',
  canceled: 'Cancelada',
  suspended: 'Suspensa',
};

const STATUS_BADGE_CLASS: Record<AccountSubscriptionStatus, string> = {
  active: 'bg-emerald-500/15 text-emerald-600',
  trial: 'bg-blue-500/15 text-blue-600',
  past_due: 'bg-red-500/15 text-red-600',
  canceled: 'bg-zinc-500/15 text-[var(--fg-muted)]',
  suspended: 'bg-zinc-500/15 text-[var(--fg-muted)]',
};

function StatusBadge({ status }: { status: AccountSubscriptionStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_BADGE_CLASS[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

const INVOICE_STATUS_LABEL: Record<string, string> = {
  paid: 'Paga',
  open: 'Em aberto',
  void: 'Anulada',
  draft: 'Rascunho',
  uncollectible: 'Incobrável',
};

const INVOICE_STATUS_CLASS: Record<string, string> = {
  paid: 'bg-emerald-500/15 text-emerald-600',
  open: 'bg-amber-500/15 text-amber-600',
  void: 'bg-zinc-500/15 text-[var(--fg-muted)]',
  draft: 'bg-zinc-500/15 text-[var(--fg-muted)]',
  uncollectible: 'bg-red-500/15 text-red-600',
};

function InvoiceStatusBadge({ status }: { status: string | null }) {
  const key = status ?? '';
  const label = INVOICE_STATUS_LABEL[key] ?? (status || '—');
  const cls = INVOICE_STATUS_CLASS[key] ?? 'bg-zinc-500/15 text-[var(--fg-muted)]';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function planFeatureLabel(f: unknown): string | null {
  if (typeof f === 'string') return f;
  if (f && typeof f === 'object' && 'label' in f) {
    const label = (f as { label?: unknown }).label;
    return typeof label === 'string' ? label : null;
  }
  return null;
}

/** Card de selecao de plano (fluxo "Escolher plano" — sub.plan === null). */
function PlanPickerCard({
  plan,
  selected,
  onSelect,
}: {
  plan: PublicPlan;
  selected: boolean;
  onSelect: () => void;
}) {
  const features = plan.features
    .map(planFeatureLabel)
    .filter((f): f is string => Boolean(f));

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full rounded-lg border p-4 text-left transition-colors ${
        selected
          ? 'border-[var(--accent)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]'
          : 'border-[var(--border)] hover:border-[var(--fg-muted)]'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[var(--fg)]">{plan.name}</p>
          {plan.description && (
            <p className="mt-0.5 text-xs text-[var(--fg-muted)]">{plan.description}</p>
          )}
        </div>
        <div
          aria-hidden="true"
          className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border ${
            selected ? 'border-[var(--accent)] bg-[var(--accent)]' : 'border-[var(--border)]'
          }`}
        />
      </div>

      <p className="mt-2 text-lg font-semibold text-[var(--fg)]">
        {formatCents(plan.priceCents)}
        <span className="text-sm font-normal text-[var(--fg-muted)]">
          {INTERVAL_LABEL[plan.interval] ?? `/${plan.interval}`}
        </span>
      </p>

      {plan.trialDays > 0 && (
        <p className="mt-1 text-xs font-medium text-blue-600 dark:text-blue-400">
          {plan.trialDays} dias grátis
        </p>
      )}

      {features.length > 0 && (
        <ul className="mt-3 space-y-1">
          {features.slice(0, 5).map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-1.5 text-xs text-[var(--fg-muted)]"
            >
              <Check className="mt-0.5 h-3 w-3 shrink-0 text-[var(--accent)]" />
              {f}
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}

/** Resumo do cartao default (bandeira + ultimos 4 + validade) — nunca o PAN completo. */
function CardSummary({ card }: { card: AccountCardView }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-[var(--fg)]">
      <span className="rounded border border-[var(--border)] px-2 py-1 text-xs font-medium uppercase tracking-wide text-[var(--fg-muted)]">
        {card.brand}
      </span>
      <span>•••• {card.last4}</span>
      <span className="text-[var(--fg-muted)]">
        válido até {String(card.expMonth).padStart(2, '0')}/{card.expYear}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function SubscriptionPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [portalError, setPortalError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Passo B rodada 2 — escolher e assinar um plano (sub.plan === null).
  const [flow, setFlow] = useState<SubscribeFlowState>({ kind: 'choosing' });
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  // Passo B rodada 3 — trocar cartao (sub.plan != null).
  const [cardFlow, setCardFlow] = useState<CardFlowState>({ kind: 'idle' });
  const [cardError, setCardError] = useState<string | null>(null);

  // Passo B rodada 3 — retry do pagamento em atraso (status === 'past_due').
  const [retryFlow, setRetryFlow] = useState<RetryPayFlowState>({ kind: 'idle' });
  const [retryError, setRetryError] = useState<string | null>(null);

  const subscriptionQ = useQuery({
    queryKey: ['account-subscription'],
    queryFn: () => apiClient.getAccountSubscription(),
    staleTime: 30_000,
  });

  const invoicesQ = useQuery({
    queryKey: ['account-invoices'],
    queryFn: () => apiClient.getAccountInvoices(),
    staleTime: 30_000,
  });

  // Cartao default para exibir na secao "Forma de pagamento" — so faz
  // sentido buscar quando o tenant ja tem assinatura.
  const paymentMethodQ = useQuery({
    queryKey: ['account-payment-method'],
    queryFn: () => apiClient.getPaymentMethod(),
    staleTime: 30_000,
    enabled: subscriptionQ.isSuccess && Boolean(subscriptionQ.data?.plan),
  });

  // Catalogo publico de planos — so precisa carregar quando o tenant ainda
  // nao tem plano (evita fetch inutil pra quem ja assina).
  const plansQ = useQuery({
    queryKey: ['billing-plans-public'],
    queryFn: () => apiClient.listPublicPlans(),
    staleTime: 5 * 60 * 1000,
    enabled: subscriptionQ.isSuccess && !subscriptionQ.data?.plan,
  });

  // Pre-seleciona automaticamente quando so ha 1 plano ativo no catalogo.
  useEffect(() => {
    if (plansQ.data && plansQ.data.length === 1 && !selectedPlanCode) {
      setSelectedPlanCode(plansQ.data[0].code);
    }
  }, [plansQ.data, selectedPlanCode]);

  // Retorno de um redirect real do Payment Element (3DS/Pix/boleto): o
  // Stripe anexa `redirect_status` na query da returnUrl. Le uma unica vez
  // na montagem, decide se reinicia o polling, e limpa a query string (nao
  // reprocessar em re-render/refresh futuro).
  useEffect(() => {
    const status = searchParams.get('redirect_status');
    if (!status) return;

    if (status === 'succeeded' || status === 'processing') {
      setFlow({ kind: 'polling' });
    } else if (status === 'failed') {
      setSubscribeError('O pagamento não foi concluído. Tente novamente com outro cartão.');
    }

    const next = new URLSearchParams(searchParams);
    for (const key of [
      'redirect_status',
      'payment_intent',
      'payment_intent_client_secret',
      'setup_intent',
      'setup_intent_client_secret',
    ]) {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling pos-confirmacao: o webhook do Stripe demora pra vincular a
  // assinatura ao tenant, entao consultamos GET /account/subscription ate
  // `plan` deixar de ser null (ou estourar o teto de tempo). Se o tenant
  // estava bloqueado por inadimplencia, passa por 'unlocking' antes de
  // voltar ao normal (rodada 4).
  useEffect(() => {
    if (flow.kind !== 'polling') return;
    return pollAccountSubscription({
      queryClient,
      isDone: (s) => Boolean(s.plan),
      onDone: () => {
        setFlow(
          useAuthStore.getState().billingBlocked
            ? { kind: 'unlocking' }
            : { kind: 'choosing' },
        );
      },
      onTimeout: () => {
        setPollTimedOut(true);
        setFlow({ kind: 'choosing' });
      },
    });
  }, [flow.kind, queryClient]);

  // Rodada 4: apos confirmar o pagamento de um tenant que estava bloqueado,
  // aguarda o gate global (billingBlocked) liberar antes de voltar ao fluxo
  // normal — sem isso o usuario veria "Assinado!" mas continuaria preso na
  // tela de bloqueio ao navegar (cache do /me ainda nao refletiu).
  useEffect(() => {
    if (flow.kind !== 'unlocking') return;
    let cancelled = false;
    void waitForBillingUnlock().then(() => {
      if (!cancelled) setFlow({ kind: 'choosing' });
    });
    return () => {
      cancelled = true;
    };
  }, [flow.kind]);

  // Polling pos-retry de fatura em atraso: aguarda o status do tenant virar
  // 'active' (o webhook confirma o pagamento e libera o tenant). Mesma
  // etapa de 'unlocking' do fluxo acima antes de voltar a 'idle'.
  useEffect(() => {
    if (retryFlow.kind !== 'polling') return;
    return pollAccountSubscription({
      queryClient,
      isDone: (s) => s.status === 'active',
      onDone: () => {
        setRetryFlow(
          useAuthStore.getState().billingBlocked
            ? { kind: 'unlocking' }
            : { kind: 'idle' },
        );
      },
      onTimeout: () => setRetryFlow({ kind: 'timeout' }),
    });
  }, [retryFlow.kind, queryClient]);

  useEffect(() => {
    if (retryFlow.kind !== 'unlocking') return;
    let cancelled = false;
    void waitForBillingUnlock().then(() => {
      if (!cancelled) setRetryFlow({ kind: 'idle' });
    });
    return () => {
      cancelled = true;
    };
  }, [retryFlow.kind]);

  async function handleSubscribeClick() {
    if (flow.kind !== 'choosing' || !selectedPlanCode) return;
    const plan = plansQ.data?.find((p) => p.code === selectedPlanCode);
    if (!plan) return;

    setSubscribeError(null);
    setPollTimedOut(false);
    setFlow({ kind: 'creating' });
    try {
      const result = await apiClient.accountSubscribe(selectedPlanCode);
      setFlow({
        kind: 'paying',
        plan,
        clientSecret: result.clientSecret,
        mode: result.mode,
      });
    } catch (e) {
      const err = e as ApiError;
      setFlow({ kind: 'choosing' });
      if (err.status === 409) {
        // Ja existe assinatura ativa — os dados na tela estao desatualizados,
        // recarrega para refletir o estado real (some a secao de escolha).
        void subscriptionQ.refetch();
        return;
      }
      setSubscribeError(
        err.message ||
          (err.status === 400
            ? 'Não foi possível iniciar a assinatura para este plano.'
            : 'Não foi possível iniciar a assinatura agora. Tente novamente.'),
      );
    }
  }

  async function handleOpenPortal() {
    setPortalError(null);
    setPortalLoading(true);
    try {
      const { url } = await apiClient.createBillingPortalSession();
      window.location.href = url;
      // Sem reset de loading no sucesso: a pagina esta prestes a navegar embora.
    } catch (e) {
      const err = e as ApiError;
      setPortalError(
        err.status === 400
          ? err.message || 'Sem cadastro de cobrança.'
          : 'Não foi possível abrir o gerenciamento de pagamento. Tente novamente.',
      );
      setPortalLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Passo B rodada 3 — trocar cartao (SetupIntent + Payment Element modo setup)
  // ---------------------------------------------------------------------------

  async function handleUpdateCardClick() {
    if (cardFlow.kind !== 'idle') return;
    setCardError(null);
    setCardFlow({ kind: 'creating' });
    try {
      const { clientSecret } = await apiClient.setupPaymentMethod();
      setCardFlow({ kind: 'collecting', clientSecret });
    } catch (e) {
      const err = e as ApiError;
      setCardFlow({ kind: 'idle' });
      setCardError(
        err.message || 'Não foi possível iniciar a troca de cartão. Tente novamente.',
      );
    }
  }

  /**
   * Define o PM recem-coletado como default. O SetupIntent acabou de
   * confirmar no Stripe — em raras corridas o PM ainda nao esta 100%
   * visitavel no lado da API quando chamamos /payment-method/default logo
   * em seguida (400). Reintenta 1x com um pequeno atraso antes de desistir.
   */
  async function trySetDefaultPaymentMethod(
    paymentMethodId: string,
    attempt: 1 | 2 = 1,
  ): Promise<void> {
    try {
      await apiClient.setDefaultPaymentMethod(paymentMethodId);
      await paymentMethodQ.refetch();
      setCardFlow({ kind: 'idle' });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 400 && attempt === 1) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return trySetDefaultPaymentMethod(paymentMethodId, 2);
      }
      setCardFlow({ kind: 'idle' });
      setCardError(
        err.message || 'Não foi possível salvar o novo cartão. Tente novamente.',
      );
    }
  }

  function handleCardConfirmed(info: PaymentConfirmedInfo) {
    if (!info.paymentMethodId) {
      setCardFlow({ kind: 'idle' });
      setCardError('Não foi possível identificar o cartão. Tente novamente.');
      return;
    }
    setCardFlow({ kind: 'saving' });
    void trySetDefaultPaymentMethod(info.paymentMethodId);
  }

  // ---------------------------------------------------------------------------
  // Passo B rodada 3 — retry do pagamento em atraso
  // ---------------------------------------------------------------------------

  async function handleRetryPayClick() {
    if (retryFlow.kind !== 'idle' && retryFlow.kind !== 'timeout') return;
    setRetryError(null);
    setRetryFlow({ kind: 'creating' });
    try {
      const result = await apiClient.payOpenInvoice();
      if ('nothingToPay' in result) {
        setRetryFlow({ kind: 'nothing-to-pay' });
        return;
      }
      setRetryFlow({ kind: 'paying', clientSecret: result.clientSecret });
    } catch (e) {
      const err = e as ApiError;
      setRetryFlow({ kind: 'idle' });
      setRetryError(
        err.message || 'Não foi possível iniciar o pagamento. Tente novamente.',
      );
    }
  }

  function handleRetryConfirmed() {
    setRetryFlow({ kind: 'polling' });
  }

  const sub = subscriptionQ.data;
  const invoices = invoicesQ.data?.items ?? [];
  const trialLabel = sub?.trialEndsAt ? formatDateLong(sub.trialEndsAt) : null;
  const periodEndLabel = sub?.currentPeriodEnd ? formatDateLong(sub.currentPeriodEnd) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-[var(--fg)]">Assinatura</h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--fg-muted)]">
          Status do plano, próxima cobrança e histórico de faturas do escritório.
        </p>
      </div>

      {/* Banner de atraso — retry de pagamento na propria pagina (Payment Element) */}
      {sub?.status === 'past_due' && (
        <div className="space-y-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Não foi possível processar seu pagamento
              </p>
              <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-400/80">
                A última cobrança não foi concluída. Tente pagar novamente ou atualize seu
                cartão na seção "Forma de pagamento" abaixo.
              </p>
            </div>
          </div>

          {retryError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-500">
              {retryError}
            </div>
          )}

          {retryFlow.kind === 'nothing-to-pay' && (
            <p className="text-xs text-red-600/80 dark:text-red-400/80">
              Nenhuma fatura em aberto encontrada no momento. Se o problema persistir, entre
              em contato com o suporte.
            </p>
          )}

          {(retryFlow.kind === 'polling' || retryFlow.kind === 'unlocking') && (
            <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {retryFlow.kind === 'unlocking'
                ? 'Liberando acesso, aguarde...'
                : 'Confirmando pagamento...'}
            </div>
          )}

          {retryFlow.kind === 'timeout' && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Pagamento em processamento. Atualize a página em instantes.
            </p>
          )}

          {retryFlow.kind === 'paying' ? (
            <div className="max-w-md">
              <PaymentStep
                clientSecret={retryFlow.clientSecret}
                mode="payment"
                onBack={() => setRetryFlow({ kind: 'idle' })}
                backLabel="Cancelar"
                submitLabel="Tentar pagar novamente"
                returnUrl={`${window.location.origin}/escritorio/configuracoes/assinatura`}
                onConfirmed={handleRetryConfirmed}
              />
            </div>
          ) : (
            retryFlow.kind !== 'polling' &&
            retryFlow.kind !== 'unlocking' && (
              <Button
                onClick={() => void handleRetryPayClick()}
                disabled={retryFlow.kind === 'creating'}
                className="shrink-0 bg-red-600 text-white hover:bg-red-700"
              >
                {retryFlow.kind === 'creating' ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Preparando pagamento...
                  </>
                ) : (
                  'Tentar pagar novamente'
                )}
              </Button>
            )
          )}
        </div>
      )}

      {/* Erro ao abrir o portal (400 sem cadastro de cobranca, ou falha generica) */}
      {portalError && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">
          {portalError}
        </div>
      )}

      {/* Card: Status + Plano */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-[var(--fg)]">Status da assinatura</h2>
          {subscriptionQ.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-[var(--fg-muted)]" />
          ) : sub ? (
            <StatusBadge status={sub.status} />
          ) : null}
        </div>

        {subscriptionQ.isError && (
          <p className="text-sm text-red-500">
            Não foi possível carregar sua assinatura. Tente recarregar a página.
          </p>
        )}

        {!subscriptionQ.isLoading && sub && (
          <>
            <hr className="border-[var(--border)]" />

            {sub.plan ? (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                  Plano atual
                </p>
                <p className="text-lg font-semibold text-[var(--fg)]">
                  {sub.plan.name}{' '}
                  <span className="text-base font-normal text-[var(--fg-muted)]">
                    {formatCents(sub.plan.priceCents)}
                    {INTERVAL_LABEL[sub.plan.interval] ?? `/${sub.plan.interval}`}
                  </span>
                </p>
                {trialLabel && (
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Teste até {trialLabel}
                  </p>
                )}
                {periodEndLabel && (
                  <p className="text-sm text-[var(--fg-muted)]">
                    Próxima cobrança em {periodEndLabel}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                    Plano atual
                  </p>
                  <p className="text-sm text-[var(--fg-muted)]">Sem plano ativo.</p>
                </div>

                {flow.kind === 'polling' || flow.kind === 'unlocking' ? (
                  <div className="flex items-center gap-2.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-600 dark:text-blue-400">
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    {flow.kind === 'unlocking'
                      ? 'Liberando acesso, aguarde...'
                      : 'Confirmando pagamento...'}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                      Escolher plano
                    </p>

                    {pollTimedOut && (
                      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                        Pagamento em processamento. Atualize a página em instantes.
                      </div>
                    )}

                    {subscribeError && (
                      <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-500">
                        {subscribeError}
                      </div>
                    )}

                    {flow.kind === 'paying' ? (
                      <div className="max-w-md">
                        <PaymentStep
                          plan={flow.plan}
                          clientSecret={flow.clientSecret}
                          mode={flow.mode}
                          onBack={() => setFlow({ kind: 'choosing' })}
                          backLabel="Cancelar"
                          returnUrl={`${window.location.origin}/escritorio/configuracoes/assinatura`}
                          onConfirmed={() => setFlow({ kind: 'polling' })}
                        />
                      </div>
                    ) : (
                      <>
                        {plansQ.isLoading && (
                          <p className="text-sm text-[var(--fg-muted)]">
                            Carregando planos...
                          </p>
                        )}
                        {plansQ.isError && (
                          <p className="text-sm text-red-500">
                            Não foi possível carregar os planos disponíveis.
                          </p>
                        )}
                        {plansQ.data && plansQ.data.length === 0 && (
                          <p className="text-sm text-[var(--fg-muted)]">
                            Nenhum plano disponível no momento.
                          </p>
                        )}
                        {plansQ.data && plansQ.data.length > 0 && (
                          <>
                            <div className="grid gap-3 sm:grid-cols-2">
                              {plansQ.data.map((plan) => (
                                <PlanPickerCard
                                  key={plan.code}
                                  plan={plan}
                                  selected={selectedPlanCode === plan.code}
                                  onSelect={() => setSelectedPlanCode(plan.code)}
                                />
                              ))}
                            </div>
                            <Button
                              onClick={() => void handleSubscribeClick()}
                              disabled={!selectedPlanCode || flow.kind === 'creating'}
                              className="shrink-0"
                            >
                              {flow.kind === 'creating' ? (
                                <>
                                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                                  Preparando pagamento...
                                </>
                              ) : (
                                'Assinar'
                              )}
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Card: Forma de pagamento — so faz sentido com assinatura ativa */}
      {!subscriptionQ.isLoading && sub?.plan && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[var(--fg-muted)]" />
            <h2 className="text-sm font-semibold text-[var(--fg)]">Forma de pagamento</h2>
          </div>

          {cardError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {cardError}
            </div>
          )}

          {cardFlow.kind === 'collecting' ? (
            <div className="max-w-md">
              <PaymentStep
                clientSecret={cardFlow.clientSecret}
                mode="setup"
                onBack={() => setCardFlow({ kind: 'idle' })}
                backLabel="Cancelar"
                submitLabel="Salvar cartão"
                returnUrl={`${window.location.origin}/escritorio/configuracoes/assinatura`}
                onConfirmed={handleCardConfirmed}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {paymentMethodQ.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-[var(--fg-muted)]" />
              ) : paymentMethodQ.data?.card ? (
                <CardSummary card={paymentMethodQ.data.card} />
              ) : (
                <p className="text-sm text-[var(--fg-muted)]">Nenhum cartão cadastrado.</p>
              )}

              <Button
                variant="outline"
                onClick={() => void handleUpdateCardClick()}
                disabled={cardFlow.kind === 'creating' || cardFlow.kind === 'saving'}
                className="shrink-0"
              >
                {cardFlow.kind === 'creating' || cardFlow.kind === 'saving' ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    {cardFlow.kind === 'saving' ? 'Salvando...' : 'Abrindo...'}
                  </>
                ) : (
                  'Atualizar forma de pagamento'
                )}
              </Button>
            </div>
          )}

          {cardFlow.kind !== 'collecting' && (
            <div className="border-t border-[var(--border)] pt-3">
              <button
                type="button"
                onClick={() => void handleOpenPortal()}
                disabled={portalLoading}
                className="text-xs text-[var(--fg-muted)] underline-offset-2 hover:text-[var(--fg)] hover:underline disabled:opacity-50"
              >
                {portalLoading ? 'Abrindo portal...' : 'Abrir portal da Stripe'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Card: Historico de faturas */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-[var(--fg-muted)]" />
          <h2 className="text-sm font-semibold text-[var(--fg)]">Histórico de faturas</h2>
        </div>

        <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-xs uppercase text-[var(--fg-muted)]">
              <tr>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {invoicesQ.isLoading && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-[var(--fg-muted)]">
                    Carregando...
                  </td>
                </tr>
              )}
              {invoicesQ.isError && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-red-500">
                    Não foi possível carregar as faturas.
                  </td>
                </tr>
              )}
              {!invoicesQ.isLoading && !invoicesQ.isError && invoices.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-[var(--fg-muted)]">
                    Nenhuma fatura encontrada.
                  </td>
                </tr>
              )}
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-t border-[var(--border)] hover:bg-[color-mix(in_oklch,var(--fg)_3%,transparent)]"
                >
                  <td className="px-3 py-2 tabular-nums text-[var(--fg)]">
                    {formatDateShort(inv.created)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[var(--fg)]">
                    {formatCents(inv.status === 'paid' ? inv.amountPaid : inv.amountDue)}
                  </td>
                  <td className="px-3 py-2">
                    <InvoiceStatusBadge status={inv.status} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-3 text-xs">
                      {inv.hostedInvoiceUrl && (
                        <a
                          href={inv.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
                        >
                          Ver fatura <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {inv.invoicePdf && (
                        <a
                          href={inv.invoicePdf}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[var(--fg-muted)] hover:text-[var(--fg)] hover:underline"
                        >
                          PDF <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
